import { first } from 'lodash';
import { EllipseName } from '../enums/ellipses';
import { HitterSafety, HitterSide } from '../enums/hitters.enums';
import { PlateWarning } from '../enums/plate.enums';
import {
  EMPTY_PLATE_LOC_COV,
  ICovarianceSummary,
  IEllipse,
  IEllipseLimits,
  IEllipseScaling,
  IPlateCovariance,
} from '../interfaces/i-ellipses';
import { IHitter } from '../interfaces/i-hitter';
import { IMachineState } from '../interfaces/i-machine-state';
import {
  IPlateLoc,
  IPlateLocExt,
  ISafeLocation,
  ITrajectory,
} from '../interfaces/pitches';
import { IAziAltCoordinate } from '../interfaces/pitches/i-base';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { MIN_SHOTS_TO_ROTATE } from './ball.helper';
import {
  DEG_TO_RAD,
  getEchelonForm_2x2,
  mean,
  RAD_RIGHT_ANGLE,
} from './math.utilities';
import { TrajHelper } from './trajectory.helper';

const CLASS_NAME = 'EllipseHelper';

export const SZ_WIDTH_FT = 17 / 12;

export class EllipseHelper {
  static MAX_SCALING: IEllipseScaling = {
    name: EllipseName.Conf95,
    factor: 5.991,
    opacity: 0.3,
  };

  static MID_SCALING: IEllipseScaling = {
    name: EllipseName.Conf85,
    factor: 3.79,
    opacity: 0.2,
  };

  static MIN_SCALING: IEllipseScaling = {
    name: EllipseName.Conf50,
    factor: 1.386,
    opacity: 0.1,
  };

  // for use with hitter safety tolerances
  static SAFETY_SCALING: IEllipseScaling = {
    name: EllipseName.Conf99,
    factor: 9.21,
    opacity: 0,
    drawBorder: true,
  };

  // todo: deprecate unused ones if we never need to draw these
  // this is only for determining what is drawn on the plate view
  static NON_EMPTY_SCALINGS = [
    // this.MIN_SCALING,
    // this.MID_SCALING,
    this.MAX_SCALING,
    // this.SAFETY_SCALING,
  ];

  static rotatePoint = (config: {
    origin: IPlateLoc;
    point: IPlateLoc;
    angle_radians: number;
  }): IPlateLoc => {
    const transPoint: IPlateLoc = {
      plate_x: config.point.plate_x - config.origin.plate_x,
      plate_z: config.point.plate_z - config.origin.plate_z,
    };

    const rotated: IPlateLoc = {
      plate_x:
        transPoint.plate_x * Math.cos(config.angle_radians) -
        transPoint.plate_z * Math.sin(config.angle_radians),
      plate_z:
        transPoint.plate_z * Math.cos(config.angle_radians) +
        transPoint.plate_x * Math.sin(config.angle_radians),
    };

    const output: IPlateLoc = {
      plate_x: rotated.plate_x + config.origin.plate_x,
      plate_z: rotated.plate_z + config.origin.plate_z,
    };

    return output;
  };

  /** result will be <1 if point is inside, 1 if point is on, or >1 if point is outside */
  static checkPointInEllipse = (config: {
    origin: IPlateLoc;
    /** location post rotation */
    point: IPlateLoc;
    ellipse: IEllipse;
    /** used for scaling ellipse size */
    factor: number;
    /** if true, point needs to be rotated about the origin by the negative of ellipse rotation before comparing to the unrotated version of the ellipse */
    isPointRotated: boolean;
  }): number => {
    const urPoint = config.isPointRotated
      ? this.rotatePoint({
          origin: config.origin,
          point: config.point,
          angle_radians: -config.ellipse.angle_radians,
        })
      : config.point;

    /** check if un-rotated point is in un-rotated ellipse */
    const result =
      Math.pow(urPoint.plate_x - config.origin.plate_x, 2) /
        (config.ellipse.majorRadius * config.factor) +
      Math.pow(urPoint.plate_z - config.origin.plate_z, 2) /
        (config.ellipse.minorRadius * config.factor);

    return result;
  };

  static getEllipseContainingPoint = (config: {
    origin: IPlateLoc;
    point: IPlateLoc;
    ellipse: IEllipse;
    /** if true, point will be rotated about the origin by the negative of ellipse rotation before comparing to the unrotated version of the ellipse at different scalings */
    isPointRotated: boolean;
  }): IEllipseScaling | undefined => {
    const [min, mid, max] = [
      this.MIN_SCALING,
      this.MID_SCALING,
      this.MAX_SCALING,
    ].map((scaling) =>
      this.checkPointInEllipse({
        origin: config.origin,
        point: config.point,
        ellipse: config.ellipse,
        isPointRotated: config.isPointRotated,
        factor: scaling.factor,
      })
    );

    if (min <= 1) {
      return this.MIN_SCALING;
    }

    if (mid <= 1) {
      return this.MID_SCALING;
    }

    if (max <= 1) {
      return this.MAX_SCALING;
    }

    /** point is outside of every scaled ellipse */
    return;
  };

  /** flip anything in the quadrant 1 or 4, leave everything else alone */
  static flipQ1AndQ4 = (radians: number): number => {
    if (Math.abs(radians) < RAD_RIGHT_ANGLE) {
      return -radians;
    }

    return radians;
  };

  static getEllipseLimits = (
    ell: IEllipse,
    scaling: number
  ): IEllipseLimits => {
    const cosAngle = Math.cos(ell.angle_radians);
    const sinAngle = Math.sin(ell.angle_radians);

    const majorScaled = Math.sqrt(ell.majorRadius * scaling);
    const minorScaled = Math.sqrt(ell.minorRadius * scaling);

    return {
      absMaxX: Math.sqrt(
        Math.pow(majorScaled, 2) * Math.pow(cosAngle, 2) +
          Math.pow(minorScaled, 2) * Math.pow(sinAngle, 2)
      ),
      absMaxY: Math.sqrt(
        Math.pow(majorScaled, 2) * Math.pow(sinAngle, 2) +
          Math.pow(minorScaled, 2) * Math.pow(cosAngle, 2)
      ),
    };
  };

  /** absolute value, in feet */
  static getSafeHitterInnerX(buffer?: HitterSafety): number | undefined {
    switch (buffer) {
      case HitterSafety.high: {
        return 15 / 12;
      }

      case HitterSafety.medium: {
        return 16.5 / 12;
      }

      case HitterSafety.low: {
        return 18 / 12;
      }

      default: {
        return undefined;
      }
    }
  }

  // returning with PlateWarning.None => input location is not considered dangerous
  static getSafeHitterLoc = (config: {
    input: IPlateLoc;
    hitter: IHitter;
    limits: IEllipseLimits;
  }): ISafeLocation | undefined => {
    const FN_NAME = 'getSafeHitterLoc';

    try {
      const NO_CHANGE: ISafeLocation = {
        location: config.input,
        warning: PlateWarning.None,
      };

      if (!config.hitter.side) {
        console.debug(`${FN_NAME}: no hitter side value`);
        return NO_CHANGE;
      }

      if (!config.hitter.safety_buffer) {
        console.debug(`${FN_NAME}: no hitter safety buffer value`);
        return NO_CHANGE;
      }

      const maxAbsX = this.getSafeHitterInnerX(config.hitter.safety_buffer);
      if (maxAbsX === undefined) {
        console.debug(`${FN_NAME}: no max absolute x value`);
        return NO_CHANGE;
      }

      if (config.hitter.side === HitterSide.LHH) {
        // hitter is on right side of plate view, no problem if plate_x + ellipse limit x is less than (left of) +ve maxAbsX
        if (config.input.plate_x + config.limits.absMaxX < maxAbsX) {
          console.debug(`${FN_NAME}: plate + max is safe (LHH)`);
          return NO_CHANGE;
        }
      }

      if (config.hitter.side === HitterSide.RHH) {
        // hitter is on left side of plate view, no problem if plate_x - ellipse limit x is greater than (right of) -ve maxAbsX
        if (config.input.plate_x - config.limits.absMaxX > -maxAbsX) {
          console.debug(`${FN_NAME}: plate + max is safe (RHH)`);
          return NO_CHANGE;
        }
      }

      return {
        // todo: provide a location to recommend a safe alternative (i.e. if we need to snap)
        location: undefined,
        warning: PlateWarning.DangerousHorizontal,
      };
    } catch (e) {
      console.error(FN_NAME, e);
    }
  };

  /** determine if position adjustment needs to be recommended to avoid spray exceeding horizontal limits of strike zone */
  static getSafeEllipseLoc = (config: {
    input: IPlateLoc;
    limits: IEllipseLimits;
  }): ISafeLocation | undefined => {
    const FN_NAME = 'getSafeEllipseLoc';

    try {
      /** i.e. 17 inches for home plate, halved */
      const HALF_PLATE_FT = 0.71;

      const exceedsRight =
        config.input.plate_x + config.limits.absMaxX > HALF_PLATE_FT;

      const exceedsLeft =
        config.input.plate_x - config.limits.absMaxX < -HALF_PLATE_FT;

      /** both safePosition and limits should be given in feet */
      if (exceedsRight && exceedsLeft) {
        /** middle is safest since the ellipse is too wide */
        const result: ISafeLocation = {
          location: {
            plate_x: 0,
            plate_z: config.input.plate_z,
          },
          warning: PlateWarning.EllipseBoth,
        };

        console.debug(`${FN_NAME}: exceeds left and right limits`, result);
        return result;
      }

      if (exceedsRight) {
        const nextX = HALF_PLATE_FT - config.limits.absMaxX;

        const result: ISafeLocation = {
          location: {
            // avoid over correction by going to middle
            plate_x: nextX - config.limits.absMaxX < -HALF_PLATE_FT ? 0 : nextX,
            plate_z: config.input.plate_z,
          },
          warning: PlateWarning.EllipseRight,
        };

        console.debug(`${FN_NAME}: exceeds right limit`, result);
        return result;
      }

      if (exceedsLeft) {
        const nextX = -HALF_PLATE_FT + config.limits.absMaxX;

        const result: ISafeLocation = {
          location: {
            // avoid over correction by going to middle
            plate_x: nextX + config.limits.absMaxX > HALF_PLATE_FT ? 0 : nextX,
            plate_z: config.input.plate_z,
          },
          warning: PlateWarning.EllipseLeft,
        };

        console.debug(`${FN_NAME}: exceeds left limit`, result);
        return result;
      }

      const result: ISafeLocation = {
        location: undefined,
        warning: PlateWarning.None,
      };

      console.debug(`${FN_NAME}: no change`, result);
      return result;
    } catch (e) {
      console.error(FN_NAME, e);
    }
  };

  // returns undefined if there's an error
  static getSafeOverallLoc = (
    config: {
      input: IPlateLoc;
      // whether to check ellipse-only warnings, i.e. if there are no hitter warnings
      ellipseWarnings: boolean;
      ellipse: IEllipse;
      hitter?: IHitter;
    },
    /** used for scaling ellipse size */
    factor: number = EllipseHelper.SAFETY_SCALING.factor
  ): ISafeLocation | undefined => {
    const FN_NAME = 'getSafeOverallLoc';

    try {
      if (!config.ellipse) {
        // shouldn't trigger, nothing can be checked if ellipse is not provided
        throw new Error(`${FN_NAME}: no ellipse provided`);
      }

      const limits = this.getEllipseLimits(config.ellipse, factor);

      if (config.hitter) {
        const safeHitter = this.getSafeHitterLoc({
          input: config.input,
          hitter: config.hitter,
          limits: limits,
        });

        if (safeHitter && safeHitter.warning !== PlateWarning.None) {
          return safeHitter;
        }
      }

      if (config.ellipseWarnings) {
        const safeEllipse = this.getSafeEllipseLoc({
          input: config.input,
          limits: limits,
        });

        if (safeEllipse && safeEllipse.warning !== PlateWarning.None) {
          return safeEllipse;
        }
      }

      // fallback
      const NO_CHANGE: ISafeLocation = {
        location: undefined,
        warning: PlateWarning.None,
      };

      return NO_CHANGE;
    } catch (e) {
      console.error(FN_NAME, e);
    }
  };

  static getRotatedShots = (config: {
    ms: IMachineState;
    shots: IMachineShot[];
    releaseFt: number;
  }): IMachineShot[] => {
    const refTilt = config.ms.tilt;
    const refYaw = config.ms.yaw;

    return config.shots.map((shot) => {
      const oTraj: ITrajectory = {
        ...shot.traj,
        px: config.ms.px,
        pz: config.ms.pz,
        py: config.releaseFt,
      };

      const delta: IAziAltCoordinate = {
        altitude_rad: (refTilt - shot.actual_ms.tilt) * DEG_TO_RAD,
        azimuth_rad: (refYaw - shot.actual_ms.yaw) * DEG_TO_RAD,
      };

      const rTraj = TrajHelper.rotateTraj(oTraj, delta);
      const rShot: IMachineShot = {
        ...shot,
        traj: rTraj,
      };

      return rShot;
    });
  };

  static getRotatedLocations = (config: {
    ms: IMachineState;
    shots: IMachineShot[];
    releaseFt: number;
  }): IPlateLocExt[] => {
    const rotatedShots = this.getRotatedShots(config);

    const output = rotatedShots.map((s) => {
      const rPlate: IPlateLocExt = {
        ...TrajHelper.getPlateLoc(s.traj),
        _created: s._created,
        _id: s._id,
      };

      return rPlate;
    });

    return output;
  };

  static getEllipseFromCov(config: {
    cov: IPlateCovariance;
    sampleSize: number;
  }): IEllipse | undefined {
    const CONTEXT_NAME = `${CLASS_NAME}.getEllipseFromCov`;
    const { cov, sampleSize } = config;

    const halfTrace = (cov.xx + cov.zz) / 2;
    const determinant = cov.xx * cov.zz - Math.pow(cov.xz, 2);
    const discriminant = Math.pow(halfTrace, 2) - determinant;

    if (discriminant < 0) {
      console.warn({
        event: `${CONTEXT_NAME}: encountered a negative e-discriminant, skipping ellipse computation`,
        covariance: cov,
        discriminant,
      });

      return undefined;
    }

    const spread = Math.sqrt(discriminant);
    const majorRadius = halfTrace + spread;
    const minorRadius = halfTrace - spread;

    const echelon = getEchelonForm_2x2([
      [cov.xx - majorRadius, cov.xz],
      [cov.xz, cov.zz - majorRadius],
    ]);

    const eigRow = first(echelon);

    if (!eigRow) {
      console.warn({
        event: `${CONTEXT_NAME}: failed to compute echelon form, skipping ellipse computation`,
        covariance: cov,
        discriminant,
        majorRadius,
        minorRadius,
        echelon,
      });

      return undefined;
    }

    const eigVector = [...eigRow];
    eigVector.sort((a, b) => (Math.abs(a) > Math.abs(b) ? -1 : 1));

    const angle_radians =
      cov.xz === 0
        ? cov.xx >= cov.zz
          ? 0
          : Math.PI / 2
        : cov.xz < 0
          ? -Math.atan2(majorRadius - cov.xx, cov.xz)
          : Math.atan2(majorRadius - cov.xx, cov.xz);

    return {
      sampleSize,
      angle_radians,
      majorRadius,
      minorRadius,
      eigVector,
    };
  }

  static getCovSummaryFromCov(config: {
    cov: IPlateCovariance;
    plate: IPlateLoc;
    // i.e. number of shots
    sampleSize: number;
  }) {
    const CONTEXT_NAME = `${CLASS_NAME}.getCovSummaryFromCov`;

    try {
      const { cov, plate } = config;

      const output: ICovarianceSummary = {
        covariance: [
          [cov.xx, cov.xz],
          [cov.xz, cov.zz],
        ],
        std_location: {
          plate_x: Math.sqrt(cov.xx),
          plate_z: Math.sqrt(cov.zz),
        },
        mean_location: plate,
      };

      output.ellipse = this.getEllipseFromCov({
        cov,
        sampleSize: config.sampleSize,
      });

      return output;
    } catch (e) {
      console.error(`${CONTEXT_NAME}: encountered an error`, e);
      return EMPTY_PLATE_LOC_COV;
    }
  }

  static getCovSummaryFromShots = (config: {
    ms: IMachineState;
    shots: IMachineShot[];
    releaseFt: number;
  }): ICovarianceSummary => {
    const CONTEXT_NAME = `${CLASS_NAME}.getCovSummaryFromShots`;

    try {
      if (config.shots.length < MIN_SHOTS_TO_ROTATE) {
        return EMPTY_PLATE_LOC_COV;
      }

      const rotatedLocs: IPlateLoc[] =
        EllipseHelper.getRotatedLocations(config);

      const meanX = mean(rotatedLocs.map((l) => l.plate_x));
      const meanZ = mean(rotatedLocs.map((l) => l.plate_z));

      const xDeltas = rotatedLocs.map((l) => l.plate_x - meanX);
      const zDeltas = rotatedLocs.map((l) => l.plate_z - meanZ);

      const nMinus1 = rotatedLocs.length - 1;

      const cov: IPlateCovariance = {
        xx: mean(
          xDeltas.map((_, i) => xDeltas[i] * xDeltas[i]),
          nMinus1
        ),
        xz: mean(
          xDeltas.map((_, i) => xDeltas[i] * zDeltas[i]),
          nMinus1
        ),
        zz: mean(
          xDeltas.map((_, i) => zDeltas[i] * zDeltas[i]),
          nMinus1
        ),
      };

      const output = this.getCovSummaryFromCov({
        cov: cov,
        plate: {
          plate_x: meanX,
          plate_z: meanZ,
        },
        sampleSize: config.shots.length,
      });

      return output;
    } catch (e) {
      console.error(`${CONTEXT_NAME}: encountered an error`, e);
      return EMPTY_PLATE_LOC_COV;
    }
  };
}
